import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Mail, MessageCircleQuestion, Search, Sparkles } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { Modal, TextInput } from '@/components/ui';
import { useT } from '@/i18n/context';
import { ask, type AssistantEntry, type AssistantResult } from '@/help/assistant';
import { buildEntries, type AssistantMode } from '@/help/assistant-entries';
import { HELP } from '@/help/content';
import { closeAssistant, closeReportProblem, useAssistantState, useReportProblemOpen } from '@/help/assistant-store';
import { ReportProblemDialog } from '@/components/ReportProblem';
import { visibleNavItems } from '@/layouts/mobile-nav';
import { api } from '@/lib/api';

/**
 * «اسأل المساعد» (29 سبتمبر 2026): سؤالٌ بكلماتك — بأي لهجة وبأخطاء إملاء — يُجاب من الدليل نفسِه
 * (`help/content.ts`) بمعجمٍ ومطابقةٍ تقريبية تعمل **في المتصفّح وحده** (`help/assistant.ts`): لا كلمةٌ
 * تغادر الجهاز، ولا خدمةَ ذكاءٍ خارجية. ما يُجاب عليه بثقة يُعرض جواباً واحداً بخطواته وزرِّ «افتح
 * الشاشة»؛ وما لم يُفهم يُعرض «أقرب ما وجدت» ثلاثةً. ولا يُجاب أحدٌ عن شاشةٍ لا يملكها: المدخلاتُ من
 * قائمته هو. وما لم يجد له المساعدُ جواباً — أو قال صاحبُه «لم يفدني» — يصل إدارةَ المنصة مقنَّعاً بلا
 * هوية (`POST /help-questions`) لسدّ الثغرة في المعجم.
 *
 * الفتحُ من كل مكان: «؟» في الترويسة، «المزيد» على الجوال، ترويسةُ منصة الطفل، الترويسةُ العلنية،
 * صفحاتُ الدخول، لوحةُ البحث حين لا تجد، ومفتاحُ `?` خارج الحقول. النافذةُ تُرسم مرّةً في `App`.
 */
const SUGGEST: Record<AssistantMode, string[]> = {
  auth: ['كيف أحيل طالباً؟', 'كيف أسجّل الحضور؟', 'أين جدولي؟', 'كيف أضيف طفلي؟', 'نسيت كلمة المرور'],
  public: ['أنا وليّ أمر — من أين أبدأ؟', 'كيف أسجّل مركزي؟', 'أنا أخصائي وأريد حساباً', 'لم يصلني رمز التحقق'],
  kid: ['وين الألعاب؟', 'كيف أسلّم الواجب؟', 'كيف أكلّم المعلّمة؟'],
};

function useMode(): AssistantMode {
  const { status, me } = useAuth();
  const { pathname } = useLocation();
  if (status !== 'authenticated') return 'public';
  if (me?.student || pathname.startsWith('/me/student')) return 'kid';
  return 'auth';
}

export function HelpAssistant() {
  const st = useAssistantState();
  const report = useReportProblemOpen();
  const { status } = useAuth();
  return (
    <>
      {st.open && <AssistantDialog initialQuestion={st.initialQuestion} />}
      {report && status === 'authenticated' && <ReportProblemDialog onClose={closeReportProblem} />}
    </>
  );
}

function AssistantDialog({ initialQuestion }: { initialQuestion: string }) {
  const mode = useMode();
  const { me, roles } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [term, setTerm] = useState(initialQuestion);
  const [asked, setAsked] = useState(initialQuestion.trim());
  const [feedback, setFeedback] = useState<'none' | 'sent'>('none');
  const reported = useRef<string | null>(null);

  const entries = useMemo<AssistantEntry[]>(() => {
    if (mode !== 'auth') return buildEntries(mode);
    const screens = visibleNavItems({ roles, studentKind: me?.student?.kind ?? null }).map((i) => ({ to: i.to, title: t(i.labelId) }));
    return buildEntries('auth', screens);
  }, [mode, roles, me?.student?.kind, t]);

  const result = useMemo<AssistantResult | null>(() => (asked ? ask(asked, entries) : null), [asked, entries]);
  const roleName = roles[0] ?? null;

  // ما لم يُجب يصل الخادمَ مرّةً لكل سؤالٍ مطروح — بعد الطرح لا مع كل حرف
  useEffect(() => {
    if (!result || !asked || result.kind === 'answer' || reported.current === asked) return;
    reported.current = asked;
    void api.post('/help-questions', { question: asked.slice(0, 300), mode, kind: result.kind, role: roleName ?? undefined, matchedKey: result.hits[0]?.entry.key }, { anonymous: true, institutionId: null }).catch(() => undefined);
  }, [result, asked, mode, roleName]);

  const submit = () => { const q = term.trim(); setAsked(q); setFeedback('none'); };
  const go = (to: string) => { closeAssistant(); navigate(to); };
  const unhelpful = () => {
    setFeedback('sent');
    void api.post('/help-questions', { question: asked.slice(0, 300), mode, kind: 'unhelpful', role: roleName ?? undefined, matchedKey: result?.hits[0]?.entry.key }, { anonymous: true, institutionId: null }).catch(() => undefined);
  };
  const helpBase = mode === 'kid' ? '/me/student/help' : '/help';
  const contactTo = mode === 'kid' ? '/me/student/inbox' : '/contact';

  return (
    <Modal title="اسأل المساعد" description="اكتب سؤالك بكلماتك — بأي لهجة. الجوابُ من دليل المنصة، ولا يغادر سؤالُك جهازك." onClose={closeAssistant}>
      <div className="stack gap-4 assistant" data-testid="assistant" data-mode={mode}>
        <form className="assistant__ask" onSubmit={(e) => { e.preventDefault(); submit(); }}>
          <TextInput
            label="سؤالك"
            type="search" value={term} autoFocus
            placeholder="مثلاً: كيف أحيل طالباً؟"
            onChange={(e) => setTerm(e.target.value)}
            wrapClassName="grow"
            data-testid="assistant-input"
          />
          <button type="submit" className="btn btn--primary assistant__go" aria-label="اسأل" title="اسأل"><Search size={16} aria-hidden /><span>اسأل</span></button>
        </form>

        {!result && (
          <div className="assistant__suggest" aria-label="أسئلةٌ شائعة">
            <p className="caption">جرّب أحد هذه الأسئلة:</p>
            <div className="assistant__chips">
              {SUGGEST[mode].map((q) => (
                <button key={q} type="button" className="chip assistant__chip" onClick={() => { setTerm(q); setAsked(q); setFeedback('none'); }}>{q}</button>
              ))}
            </div>
          </div>
        )}

        {result?.kind === 'empty' && (
          <p className="note assistant__empty" role="status">لم أفهم السؤال — اكتب ما تريد فعله بكلمةٍ أو كلمتين، مثل «إحالة» أو «الحضور» أو «كلمة المرور».</p>
        )}

        {result?.kind === 'answer' && result.hits[0] && (
          <Answer hit={result.hits[0].entry} go={go} helpBase={helpBase} pathname={pathname} big />
        )}

        {result?.kind === 'nearest' && result.hits.length > 0 && (
          <div className="stack gap-2" role="status">
            <p className="assistant__nearest-title"><Sparkles size={16} aria-hidden /> لم أفهم السؤال تماماً — أقرب ما وجدت:</p>
            {result.hits.map((h) => <Answer key={h.entry.key} hit={h.entry} go={go} helpBase={helpBase} pathname={pathname} />)}
          </div>
        )}

        {result && result.kind !== 'empty' && (
          <div className="assistant__foot">
            {feedback === 'sent'
              ? <span className="caption" role="status">شكراً — وصلت ملاحظتُك، وسيُضاف سؤالك إلى الدليل.</span>
              : <button type="button" className="link-btn" onClick={unhelpful}>لم يفدني الجواب</button>}
            <Link to={helpBase} className="link-btn" onClick={closeAssistant}><BookOpen size={16} aria-hidden /> مركز المساعدة</Link>
            <Link to={contactTo} className="link-btn" onClick={closeAssistant}><Mail size={16} aria-hidden /> {mode === 'kid' ? 'اكتب لمعلّمك' : 'راسلنا'}</Link>
          </div>
        )}
      </div>
    </Modal>
  );
}

function Answer({ hit, go, helpBase, pathname, big = false }: { hit: AssistantEntry; go: (to: string) => void; helpBase: string; pathname: string; big?: boolean }) {
  const here = hit.to === pathname;
  return (
    <article className={`assistant__card${big ? ' assistant__card--big' : ''}`} data-testid="assistant-answer" data-key={hit.key}>
      <h3 className="assistant__title"><MessageCircleQuestion size={18} aria-hidden /> {hit.title}</h3>
      <p className="assistant__what">{hit.what}</p>
      {big && hit.steps.length > 0 && (
        <ol className="help-steps">{hit.steps.map((s) => <li key={s}>{s}</li>)}</ol>
      )}
      {big && hit.tips && hit.tips.length > 0 && (
        <ul className="help-tips">{hit.tips.map((s) => <li key={s}>{s}</li>)}</ul>
      )}
      <div className="assistant__actions">
        {hit.to && !here && <button type="button" className="btn btn--primary btn--sm" onClick={() => go(hit.to as string)}><ArrowLeft size={14} aria-hidden /> افتح الشاشة</button>}
        {hit.to && here && <span className="caption">أنت على هذه الشاشة الآن.</span>}
        {hit.to && HELP[hit.to] && <Link to={`${helpBase}?page=${encodeURIComponent(hit.to)}`} className="link-btn" onClick={closeAssistant}>اقرأ في مركز المساعدة</Link>}
      </div>
    </article>
  );
}
